/**
 * Overview — the answer, before the evidence.
 *
 * Volume totals say how busy you were; they do not say whether the service is
 * getting cheaper or more expensive to run. So the first row here is unit
 * economics — what one request, document and page costs, and what a page
 * really costs you once the plan fee is spread over it — and only then the
 * breakdown of where the money went.
 */
import { useState } from 'react'
import { ArrowRight, ChevronRight, Info, Layers, Wallet } from 'lucide-react'
import { Card, CountUp, EmptyState, HelpTip, ShareBar } from './BillingPrimitives'
import InsightRail from './InsightRail'
import CostDriverDrawer from './CostDriverDrawer'
import { AI_CHARGES_HELP, costDrivers, formatInt, formatUsdAs, unitEconomics } from '../../utils/billing'
import { percentParts } from '../../utils/apportion'

function UnitCard({ metric, index }) {
  const hasValue = metric.value !== null && metric.value !== undefined
  // "No activity" only when there really was none: a plan with no per-page
  // price still shows what it is and how many pages it processed.
  const foot = hasValue || metric.text ? metric.foot : null

  return (
    <div
      className={`relative rounded-xl border p-4 animate-card-in transition-colors
        ${metric.primary
          ? 'border-navy-600/40 dark:border-navy-400/30 bg-gradient-to-br from-navy-50/70 to-transparent dark:from-navy-900/20'
          : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'}`}
      style={{ animationDelay: `${60 + index * 50}ms` }}
    >
      <div className="flex items-start gap-2">
        <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{metric.label}</p>
        <span className="ml-auto"><HelpTip>{metric.help}</HelpTip></span>
      </div>
      <p className="mt-2 text-[26px] font-bold text-gray-900 dark:text-gray-100 tracking-tight leading-none tabular-nums">
        {hasValue
          ? <CountUp value={metric.value} format={v => metric.format(v)} />
          : metric.text
            ? <span>{metric.text}</span>
            : <span className="text-gray-300 dark:text-gray-600">—</span>}
      </p>
      <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
        {foot || 'No activity yet this billing month'}
      </p>
    </div>
  )
}

export default function OverviewPanel({ summary, plan, chart, insights, onInsightAction, templates, templatesLoading, onOpenTab, displayCurrency, periodLabel }) {
  const [driverId, setDriverId] = useState(null)
  // "Where AI processing cost went" follows the page's period control; unit economics stays
  // on the billing cycle because the plan fee it spreads is a per-cycle figure.
  const scope = 'period'
  const metrics = unitEconomics({ summary, plan, displayCurrency })
  const drivers = costDrivers({ summary, chart, scope })
  const variable = drivers.filter(d => !d.fixed)
  const variableTotal = variable.reduce((sum, d) => sum + d.cost, 0)
  // Whole-number shares of the usage-driven cost that add up to exactly 100.
  const pcts = percentParts(variable.map(d => d.cost))
  const sharePct = new Map(variable.map((d, i) => [d.id, pcts[i]]))
  const hasFixed = drivers.some(d => d.fixed)

  const openDriver = drivers.find(d => d.id === driverId) || null
  const topTemplates = (templates?.slice(0, 5) || []).filter(t => t.total > 0)
  const templateMax = Math.max(1, ...topTemplates.map(t => t.total))

  return (
    <div className="space-y-5">
      <section data-tour="unit-economics">
        <div className="flex items-center gap-2 mb-3">
          <Wallet className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Unit economics</h2>
          <HelpTip>
            Totals grow with volume whatever you do. Cost per unit is the figure that tells you whether
            the service is getting cheaper or more expensive to run.
          </HelpTip>
          <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-500">Current billing month</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {metrics.map((metric, i) => <UnitCard key={metric.key} metric={metric} index={i} />)}
        </div>
      </section>

      {insights?.length > 0 && (
        <section data-tour="insights">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">What changed, and what to do</h2>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
              {insights.length} {insights.length === 1 ? 'finding' : 'findings'}
            </span>
          </div>
          <InsightRail insights={insights} onAction={onInsightAction} />
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ---- where the money went ---- */}
        <Card className="p-5 animate-card-in" style={{ animationDelay: '260ms' }}>
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Where AI processing cost went</h2>
            <span className="text-[11px] text-gray-400 dark:text-gray-500">{periodLabel}</span>
            <HelpTip>
              Estimated AI processing charges for the selected period, split by what drove them. {AI_CHARGES_HELP}
              {hasFixed && ' Platform infrastructure is a flat monthly figure rather than a share of this billing month\'s usage, so it is listed separately.'}
            </HelpTip>
          </div>

          <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 gap-0.5 mb-4">
            {drivers.filter(d => !d.fixed).map(driver => (
              <div
                key={driver.id}
                className="transition-[width] duration-700 ease-out first:rounded-l-full last:rounded-r-full"
                style={{
                  width: `${variableTotal > 0 ? (driver.cost / variableTotal) * 100 : 0}%`,
                  backgroundColor: driver.color,
                }}
              />
            ))}
          </div>

          {hasFixed && (
            <p className="text-[10.5px] text-gray-400 dark:text-gray-500 -mt-3 mb-3">
              Bar shows the split of usage-driven consumption; the fixed platform cost below is not part of it.
            </p>
          )}

          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {drivers.map(driver => {
              const share = !driver.fixed && variableTotal > 0 ? sharePct.get(driver.id) : null
              return (
                <button
                  key={driver.id}
                  type="button"
                  onClick={() => setDriverId(driver.id)}
                  aria-label={`${driver.name} details`}
                  className="group flex w-full items-center gap-3 py-2.5 text-left rounded-lg -mx-2 px-2
                             hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
                >
                  <i className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: driver.color }} />
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                      {driver.name}
                      {driver.fixed && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded
                                         bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                          Fixed
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{driver.plain}</p>
                  </div>
                  <div className="ml-auto text-right flex-shrink-0">
                    <p className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                      {formatUsdAs(driver.cost, displayCurrency)}
                    </p>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
                      {share !== null ? `${share}% · ` : ''}{driver.unit}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 flex-shrink-0 text-gray-300 dark:text-gray-600
                                           group-hover:text-gray-500 dark:group-hover:text-gray-300 transition-colors" />
                </button>
              )
            })}
          </div>

          <button
            type="button"
            onClick={() => onOpenTab('costs')}
            className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-navy-700 dark:text-navy-300
                       hover:gap-2.5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
          >
            Full cost breakdown
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </Card>

        {/* ---- busiest templates ---- */}
        <Card
          className={`p-5 animate-card-in transition-opacity duration-200 ${templatesLoading ? 'opacity-50' : 'opacity-100'}`}
          style={{ animationDelay: '320ms' }}
        >
          {templatesLoading && <span className="sr-only" role="status">Updating for the new period</span>}
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Busiest templates</h2>
            <HelpTip>
              Measured request volume per template over the selected period. Ranked by volume rather than
              cost, because consumption is recorded per processing job and never per template. The
              Templates tab shows the estimated cost allocation and explains how it is derived.
            </HelpTip>
          </div>

          {topTemplates.length === 0 ? (
            <EmptyState
              icon={Layers}
              title="No template activity in this period"
              body="Requests raised against a template will be ranked here."
            />
          ) : (
            <div className="space-y-1">
              {topTemplates.map(template => (
                <div key={template.template_id ?? template.template_name} className="flex items-center gap-3 py-1.5">
                  <span
                    className="text-[13px] font-medium text-gray-900 dark:text-gray-100 truncate w-[46%]"
                    title={template.template_name}
                  >
                    {template.template_name}
                  </span>
                  <ShareBar value={template.total} max={templateMax} />
                  <span className="text-[12px] tabular-nums text-gray-600 dark:text-gray-300 w-20 text-right flex-shrink-0">
                    {formatInt(template.total)} req
                  </span>
                </div>
              ))}
              <button
                type="button"
                onClick={() => onOpenTab('templates')}
                className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-semibold text-navy-700 dark:text-navy-300
                           hover:gap-2.5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
              >
                All templates
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </Card>
      </div>

      <CostDriverDrawer
        driver={openDriver}
        share={openDriver && !openDriver.fixed && variableTotal > 0 ? sharePct.get(openDriver.id) / 100 : 0}
        summary={summary}
        scope={scope}
        periodLabel={periodLabel}
        displayCurrency={displayCurrency}
        onClose={() => setDriverId(null)}
        onExplore={() => { setDriverId(null); onOpenTab('costs') }}
      />

      <p className="flex items-start gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        AI processing figures are estimates at Microsoft&apos;s published rates, billed by Microsoft to the Azure
        subscription that runs this app and separate from your plan, which is shown at the top of the page.
      </p>
    </div>
  )
}
